import { useEffect, type FC, type ReactNode } from "react";

import { TooltipProvider } from "@/components/ui/tooltip";

import { RequestsChatHost } from "./chat/RequestsChatHost";
import { OpenFromUrl } from "./components/OpenFromUrl";
import {
  RequestsEnvProvider,
  type RequestsEnv
} from "./context/RequestsEnvContext";
import {
  RequestsIdeProvider,
  useRequestsIde
} from "./context/RequestsIdeContext";
import {
  RequestsDataProvider,
  useRequestsData
} from "./data/RequestsDataContext";
import { RequestsStageNav } from "./explorer/RequestsStageNav";
import { useNewRequest } from "./explorer/useNewRequest";
import { requestsTitleStore } from "./layout/requestsTitleStore";
import { RequestsListPage } from "./list/RequestsListPage";
import { IntakePage } from "./pages/IntakePage";
import { ReviewPage } from "./pages/ReviewPage";
import { parseSubmissionId, type RequestArtifact } from "./types";

type ClientIdentity = {
  clientName?: string | null;
  clientEmail?: string | null;
};

type WorkspaceProps = ClientIdentity & {
  foreignNationalId: string;
  /** Firm branding for the client-facing preview. */
  lawFirmName?: string | null;
  lawFirmLogoUrl?: string | null;
  enableWhitelabelForeignNationalIntake?: boolean;
  children: ReactNode;
};

/** Honors `?new=` and legacy deep links wherever the workspace is showing. */
const DeepLinks: FC = () => {
  const newRequest = useNewRequest();
  return (
    <OpenFromUrl onNewRequest={(action) => void newRequest.start(action)} />
  );
};

/**
 * The client workspace's Requests state: the open page, intake and template
 * data, and the firm branding, shared by the stages in the profile nav, the
 * page in the center and Casey's floating panel (through `RequestsChatHost`).
 */
export const RequestsWorkspace: FC<WorkspaceProps> = ({
  foreignNationalId,
  clientName,
  clientEmail,
  lawFirmName,
  lawFirmLogoUrl,
  enableWhitelabelForeignNationalIntake,
  children
}) => {
  const env: RequestsEnv = {
    clientName: clientName ?? null,
    clientEmail: clientEmail ?? null,
    lawFirmName: lawFirmName ?? null,
    lawFirmLogoUrl: lawFirmLogoUrl ?? null,
    enableWhitelabelForeignNationalIntake: Boolean(
      enableWhitelabelForeignNationalIntake
    )
  };
  return (
    <RequestsIdeProvider clientId={foreignNationalId}>
      <RequestsDataProvider clientId={foreignNationalId}>
        <RequestsEnvProvider value={env}>
          <TooltipProvider>
            <DeepLinks />
            <RequestsChatHost />
            {children}
          </TooltipProvider>
        </RequestsEnvProvider>
      </RequestsDataProvider>
    </RequestsIdeProvider>
  );
};

/** The Requests row's children in the profile nav: the stages, filtering the list. */
export const RequestsExplorer: FC = () => {
  const newRequest = useNewRequest();
  return (
    <div className="flex flex-col" data-testid="requests-explorer">
      <RequestsStageNav
        onNewRequest={(action) => void newRequest.start(action)}
        busy={newRequest.busy}
      />
    </div>
  );
};

/** The page's name for the client header's trailing crumb. */
const usePageTitle = (artifact: RequestArtifact | null): string | null => {
  const data = useRequestsData();
  if (!artifact) return null;
  const intakeId =
    artifact.kind === "submission"
      ? parseSubmissionId(artifact.id)?.intakeId
      : artifact.id;
  const intake = data.intakes.find((i) => i.outgoing_intake_id === intakeId);
  return intake?.title || artifact.title || null;
};

/** The Requests tab's center: the list, or the page `?open=` names. */
export const RequestsCenter: FC<ClientIdentity> = ({
  clientName,
  clientEmail
}) => {
  const { activeArtifact } = useRequestsIde();
  const title = usePageTitle(activeArtifact);

  useEffect(() => {
    requestsTitleStore.set(title);
    return () => requestsTitleStore.set(null);
  }, [title]);

  if (!activeArtifact)
    return <RequestsListPage clientName={clientName ?? null} />;

  switch (activeArtifact.kind) {
    case "intake":
    case "preview":
      // One key for both, so opening the preview keeps the draft mounted.
      return (
        <IntakePage
          key={`intake:${activeArtifact.id}`}
          intakeId={activeArtifact.id}
          clientEmail={clientEmail ?? null}
          clientName={clientName ?? null}
          preview={activeArtifact.kind === "preview"}
        />
      );
    case "submission": {
      const ids = parseSubmissionId(activeArtifact.id);
      if (!ids) return <RequestsListPage clientName={clientName ?? null} />;
      return (
        <ReviewPage
          key={activeArtifact.id}
          intakeId={ids.intakeId}
          submissionId={ids.submissionId}
          clientName={clientName ?? null}
        />
      );
    }
    default:
      return null;
  }
};
